/**
 * EmailSourceForm — Add/edit a mailbox configuration.
 * Used by EmailSourcesTab for both create and edit modes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminApi } from "@/services/api/admin-api";
import type { MailboxType, OrganizationAdminListItemDto } from "@/types/api/admin";

// =============================================================================
// TYPES
// =============================================================================

interface EmailSourceFormProps {
    clientId: number;
    sourceId?: number;
    isCreate: boolean;
    onBack: () => void;
    onSaved: () => void;
}

// =============================================================================
// FORM SECTION HELPER
// =============================================================================

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h4 className="mb-3 border-b border-secondary pb-2 text-sm font-semibold text-secondary">{title}</h4>
            {children}
        </div>
    );
}

// =============================================================================
// EMAIL SOURCE FORM
// =============================================================================

export function EmailSourceForm({ clientId, sourceId, isCreate, onBack, onSaved }: EmailSourceFormProps) {
    const adminApi = useAdminApi();

    // Reference data
    const [organizations, setOrganizations] = useState<OrganizationAdminListItemDto[]>([]);

    // Load state
    const [isLoading, setIsLoading] = useState(!isCreate);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Form fields
    const [emailAddress, setEmailAddress] = useState("");
    const [mailboxType, setMailboxType] = useState<string>("User");
    const [organizationId, setOrganizationId] = useState<string>("");
    const [sourceFolder, setSourceFolder] = useState("Inbox");
    const [processedFolder, setProcessedFolder] = useState("Processed");
    const [errorFolder, setErrorFolder] = useState("Errors");
    const [pollIntervalSec, setPollIntervalSec] = useState("60");
    const [isEnabled, setIsEnabled] = useState(true);
    const [notes, setNotes] = useState("");

    // Dropdown options
    const mailboxTypeOptions: SelectItemType[] = useMemo(
        () => [
            { id: "User", label: "User" },
            { id: "Shared", label: "Shared" },
        ],
        [],
    );

    const orgOptions: SelectItemType[] = useMemo(
        () => organizations.filter((o) => o.isActive).map((o) => ({ id: String(o.id), label: `${o.name} (${o.code})` })),
        [organizations],
    );

    // Load reference data
    useEffect(() => {
        let cancelled = false;
        async function loadOrgs() {
            try {
                const orgs = await adminApi.listClientOrganizations(clientId);
                if (!cancelled) setOrganizations(orgs);
            } catch {
                // non-fatal
            }
        }
        loadOrgs();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    // Load existing record (edit mode only)
    useEffect(() => {
        if (isCreate || !sourceId) return;
        let cancelled = false;
        async function loadDetail() {
            setIsLoading(true);
            setLoadError(null);
            try {
                const d = await adminApi.getEmailSource(clientId, sourceId!);
                if (cancelled) return;
                setEmailAddress(d.emailAddress);
                setMailboxType(d.mailboxType);
                setOrganizationId(String(d.organizationId));
                setSourceFolder(d.sourceFolder);
                setProcessedFolder(d.processedFolder);
                setErrorFolder(d.errorFolder);
                setPollIntervalSec(String(Math.round(d.pollIntervalMs / 1000)));
                setIsEnabled(d.isEnabled);
                setNotes(d.notes ?? "");
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load email source.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        loadDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, sourceId, isCreate]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        const pollMs = Math.max(1000, Number(pollIntervalSec) * 1000);

        try {
            if (isCreate) {
                await adminApi.createEmailSource(clientId, {
                    organizationId: Number(organizationId),
                    emailAddress: emailAddress.trim(),
                    mailboxType: mailboxType as MailboxType,
                    sourceFolder: sourceFolder.trim(),
                    processedFolder: processedFolder.trim(),
                    errorFolder: errorFolder.trim(),
                    pollIntervalMs: pollMs,
                    notes: notes.trim() || null,
                });
                onSaved();
                onBack();
            } else {
                await adminApi.updateEmailSource(clientId, sourceId!, {
                    organizationId: organizationId ? Number(organizationId) : undefined,
                    mailboxType: mailboxType as MailboxType,
                    sourceFolder: sourceFolder.trim(),
                    processedFolder: processedFolder.trim(),
                    errorFolder: errorFolder.trim(),
                    pollIntervalMs: pollMs,
                    isEnabled,
                    notes: notes.trim() || null,
                });
                onSaved();
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save email source.");
        } finally {
            setIsSaving(false);
        }
    }, [
        adminApi,
        clientId,
        sourceId,
        isCreate,
        emailAddress,
        mailboxType,
        organizationId,
        sourceFolder,
        processedFolder,
        errorFolder,
        pollIntervalSec,
        isEnabled,
        notes,
        onSaved,
        onBack,
    ]);

    const canSave = emailAddress.trim().length > 0 && organizationId !== "";

    if (isLoading) {
        return <LoadingIndicator size="sm" label="Loading email source..." />;
    }

    if (loadError) {
        return (
            <div>
                <Button color="secondary" size="sm" iconLeading={ArrowLeft} onClick={onBack}>
                    Back to list
                </Button>
                <p className="mt-3 text-sm text-error-primary">{loadError}</p>
            </div>
        );
    }

    return (
        <div>
            <Button color="secondary" size="sm" iconLeading={ArrowLeft} onClick={onBack}>
                Back to list
            </Button>

            <h3 className="mt-4 text-lg font-semibold text-primary">{isCreate ? "Add Email Source" : "Edit Email Source"}</h3>

            <div className="mt-6 max-w-2xl space-y-8">
                <FormSection title="Mailbox">
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="Email Address"
                            placeholder="invoices@company.com"
                            value={emailAddress}
                            onChange={setEmailAddress}
                            isRequired
                            isDisabled={!isCreate}
                        />
                        <Select
                            label="Mailbox Type"
                            items={mailboxTypeOptions}
                            selectedKey={mailboxType}
                            onSelectionChange={(key) => setMailboxType(String(key))}
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    </div>
                    <div className="mt-4">
                        <Select
                            label="Organization"
                            items={orgOptions}
                            selectedKey={organizationId}
                            onSelectionChange={(key) => setOrganizationId(String(key))}
                            placeholder="Select organization..."
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    </div>
                </FormSection>

                <FormSection title="Folders">
                    <div className="grid grid-cols-3 gap-4">
                        <Input label="Source Folder" placeholder="Inbox" value={sourceFolder} onChange={setSourceFolder} />
                        <Input label="Processed Folder" placeholder="Processed" value={processedFolder} onChange={setProcessedFolder} />
                        <Input label="Error Folder" placeholder="Errors" value={errorFolder} onChange={setErrorFolder} />
                    </div>
                </FormSection>

                <FormSection title="Settings">
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Polling Interval (seconds)" type="number" placeholder="60" value={pollIntervalSec} onChange={setPollIntervalSec} />
                        {!isCreate && (
                            <Toggle label="Enabled" hint="Disabled mailboxes are not polled" isSelected={isEnabled} onChange={setIsEnabled} size="sm" />
                        )}
                    </div>
                    <div className="mt-4">
                        <TextArea label="Notes" placeholder="Optional notes about this mailbox..." value={notes} onChange={setNotes} rows={3} />
                    </div>
                </FormSection>

                <div className="flex items-center gap-3">
                    <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !canSave}>
                        {isSaving ? "Saving..." : isCreate ? "Add Email Source" : "Save Changes"}
                    </Button>
                    <Button color="secondary" size="sm" onClick={onBack}>
                        Cancel
                    </Button>
                    {saveSuccess && <span className="text-sm text-success-primary">Saved successfully.</span>}
                    {saveError && <span className="text-sm text-error-primary">{saveError}</span>}
                </div>
            </div>
        </div>
    );
}
